import { Box, Checkbox, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { DeleteUpdateDateSelection } from "../../RecurringShift/util/DeleteUpdateDateSelection";
import { useGetRecurringShift } from "../../RecurringShift/util/hooks";
import { useCallInReasonOptions } from "../../team/CallInReasons/callInReasonsHooks";
import { SystemRoundedCheckbox } from "../../UIToolkit/Clickable/SystemRoundedCheckbox";
import { DateInput } from "../../UIToolkit/Inputs/DateInput";
import { SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { BiweeklyDeleteSection } from "./BiweeklyDeleteSection";
import { DeleteRecurringOrSingleMode, type DeleteRecurringOrSingleShiftProps } from "./types";
import { useDeleteRecurringOrSingleState } from "./useDeleteRecurringOrSingleState";

const inputWidth = "300px";

export const DeleteRecurringOrSingleShift: FC<DeleteRecurringOrSingleShiftProps> = ({
	nurseInShift,
	onCancel,
	onFinished,
	source,
	showActual,
	allowRecurringModes,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const {
		track,
		handleConfirm,
		deleteValid,
		selectedModeChangeHandler,
		selectedMode,
		allowRecurringModes: canDeleteSeries,
		setDateToDeleteFrom,
		setDeleteUpdateDateSelectionValid,
		dontRenderReasonSelector,
		isSelfSchedulingShift,
		selectedReason,
		showReasonError,
		reasonShakeControls,
		handleReasonChange,
		removalMessage,
		setRemovalMessage,
		needsPayrollConfirmation,
		setDidConfirmPayrollWarning,
		isLoading,
		renderFindReplacementForShift,
		shouldFindReplacementForShift,
		setShouldFindReplacementForShift,
		isReplacementGateReady,
		replacementAdvice,
		isReplacementHintOpen,
		shouldWarnRemovingUnion,
		callInTime,
		setCallInTime,
	} = useDeleteRecurringOrSingleState({ nurseInShift, source, showActual, allowRecurringModes, onFinished, onCancel });
	// Series deletion hits a different endpoint (and a different guard), so the radios only exist for
	// callers that opted into it. With it off there is nothing to choose between and the group is gone.
	const renderModeRadios = canDeleteSeries && !!nurseInShift.recurringShiftId && !chosenDepartment.disable_edit_master_schedule;
	// Only the biweekly section reads it, so a caller that cannot reach that section does not fetch it.
	const { data: recurringShift } = useGetRecurringShift(
		chosenDepartment.department_id,
		nurseInShift.nurseId,
		canDeleteSeries ? (nurseInShift.recurringShiftId ?? undefined) : undefined,
	);
	const originalInterval = recurringShift?.recurringParams?.interval ?? 1;
	const originalStartDate = recurringShift ? DateTime.fromISO(recurringShift.startDate) : nurseInShift.startTime;
	const chosenDayOffsetFromSunday = nurseInShift.startTime.weekdayISO % 7;
	// Hospital flow skips the reason picker entirely (see useRemovalReasonState), so don't fetch.
	const { options: reasonDropdownItems, isLoading: isLoadingReasons, isError: isReasonsError } = useCallInReasonOptions({ enabled: !dontRenderReasonSelector });
	useEffect(() => {
		track("delete_single_of_recurring_opened", {
			shiftId: nurseInShift.id,
			nurseId: nurseInShift.nurseId,
		});
		return () => {
			track("delete_single_of_recurring_closed", {
				shiftId: nurseInShift.id,
				nurseId: nurseInShift.nurseId,
			});
		};
	}, [track, nurseInShift.id, nurseInShift.nurseId]);

	return (
		<SystemDialog
			open={true}
			onClose={onCancel}
			title={"Remove nurse from shift?"}
			onConfirm={handleConfirm}
			confirmDisabled={!deleteValid}
			confirmLoading={isLoading}
			confirmTooltip={!deleteValid ? "Have you filled all required fields?" : undefined}
			body={
				<Box
					sx={{
						width: "550px",
						minHeight: "300px",
						display: "flex",
						flexDirection: "column",
						gap: 2,
						padding: 2,
					}}
				>
					<FormControl sx={{ width: "100%" }}>
						<RadioGroup defaultValue={DeleteRecurringOrSingleMode.DeleteSingle} onChange={selectedModeChangeHandler}>
							<FormControlLabel
								value={DeleteRecurringOrSingleMode.DeleteSingle}
								control={<Radio data-testid="delete-mode-single" />}
								label={t("delete.recurring.single.option")}
								slotProps={{ typography: { sx: { whiteSpace: "nowrap" } } }}
							/>

							{renderModeRadios && (
								<FormControlLabel
									value={DeleteRecurringOrSingleMode.DeleteBiweeklyDay}
									control={<Radio data-testid="delete-mode-biweekly" />}
									label={t("delete.recurring.biweekly.option", { weekday: nurseInShift.startTime.toFormat("cccc") })}
									slotProps={{ typography: { sx: { whiteSpace: "nowrap" } } }}
								/>
							)}

							{renderModeRadios && (
								<FormControlLabel
									value={DeleteRecurringOrSingleMode.DeleteRecurring}
									control={<Radio data-testid="delete-mode-recurring" />}
									label={t("delete.recurring.all.option")}
									slotProps={{ typography: { sx: { whiteSpace: "nowrap" } } }}
								/>
							)}
						</RadioGroup>
					</FormControl>
					{/* Always rendered, whichever mode is picked — the call-off walkthrough's reason step points here. */}
					<Box
						data-annie-target="remove-nurse-reason"
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
							paddingLeft: selectedMode === DeleteRecurringOrSingleMode.DeleteSingle ? 4 : 0,
							minHeight: "100px",
						}}
					>
						{selectedMode === DeleteRecurringOrSingleMode.DeleteRecurring && (
							<DeleteUpdateDateSelection
								initialDate={nurseInShift.startTime}
								onDateSelectionChange={setDateToDeleteFrom}
								mode={"delete"}
								onValidChange={setDeleteUpdateDateSelectionValid}
							/>
						)}
						{selectedMode === DeleteRecurringOrSingleMode.DeleteBiweeklyDay && (
							<BiweeklyDeleteSection
								initialDate={nurseInShift.startTime}
								originalStartDate={originalStartDate}
								originalInterval={originalInterval}
								chosenDayOffsetFromSunday={chosenDayOffsetFromSunday}
								onDateChange={setDateToDeleteFrom}
								onValidChange={setDeleteUpdateDateSelectionValid}
							/>
						)}
						{selectedMode === DeleteRecurringOrSingleMode.DeleteSingle && !dontRenderReasonSelector && !isSelfSchedulingShift && (
							<motion.div
								animate={reasonShakeControls}
								style={{
									borderRadius: 8,
									border: showReasonError ? "2px solid #f44336" : "2px solid transparent",
									width: "fit-content",
								}}
							>
								<SystemDropdown
									data-testid="delete-recurring-reason-dropdown"
									selectedItem={selectedReason}
									items={reasonDropdownItems}
									selectSx={{ width: inputWidth, pr: 0 }}
									onChange={handleReasonChange}
									label={t("removal.reason.label")}
									loading={isLoadingReasons}
									disabled={isLoadingReasons}
								/>
							</motion.div>
						)}
						{/* An empty picker is indistinguishable from a department with no reasons, and the removal
						    can't proceed without one — say the read failed rather than leaving a blank control. */}
						<InlineHint open={!dontRenderReasonSelector && isReasonsError} tone="warning" dataTestId="delete-recurring-reasons-error-hint">
							{t("error.general")}
						</InlineHint>
						{selectedMode === DeleteRecurringOrSingleMode.DeleteSingle && selectedReason && (
							<SystemValidationTextField
								reserveHeightForHelperText={false}
								label={"Message"}
								value={removalMessage}
								inputSx={{ width: inputWidth }}
								onChange={(e) => setRemovalMessage(e.target.value)}
								onClear={() => setRemovalMessage("")}
							/>
						)}
						{selectedMode === DeleteRecurringOrSingleMode.DeleteSingle && selectedReason === "CALL_IN" && (
							<Box sx={{ display: "flex", gap: 2, width: inputWidth }} data-testid="call-in-time-input">
								<DateInput
									label={t("call.in.time.label")}
									value={callInTime}
									onChange={(newDate) => setCallInTime(callInTime.set({ year: newDate.year, month: newDate.month, day: newDate.day }))}
									maxDate={DateTime.now()}
									formControlSx={{ flex: 1 }}
								/>
								<LocalizationProvider dateAdapter={AdapterLuxon}>
									<TimeField
										label={t("call.in.time.time.label")}
										value={callInTime}
										onChange={(newTime) => newTime?.isValid && setCallInTime(callInTime.set({ hour: newTime.hour, minute: newTime.minute }))}
										format="HH:mm"
										sx={{ width: 110 }}
									/>
								</LocalizationProvider>
							</Box>
						)}
					</Box>
					{needsPayrollConfirmation && (
						<Box
							sx={{
								display: "flex",
								alignItems: "start",
								flexDirection: "column",
							}}
						>
							<Typography>This will remove the shift from actual working hours data. Make sure you want to delete!</Typography>
							<FormControlLabel
								onChange={(_, checked) => setDidConfirmPayrollWarning(checked)}
								control={<Checkbox />}
								label="I understand that this will affect payroll data."
							/>
						</Box>
					)}
					{/* The "look for cover" offer. Always mounted so the call-off walkthrough's replacement step has a
					    stable anchor; `:empty` keeps it out of the flex gap when it doesn't apply (a recurring delete,
					    a past shift, draft mode). Held back until the
					    recommendation is known, so the tour waits on the step before rather than trying to flip a
					    box that is still disabled and has no default yet. The name arrives with the gate, not with
					    anything the user does on the reason step, so it is marked as arriving on its own
					    (`WALKTHROUGH_ARRIVES_ON_ITS_OWN_ATTR`) and only Next moves the tour on to it. */}
					<Box
						data-walkthrough-arrives-on-its-own="true"
						data-annie-target={!renderFindReplacementForShift || isReplacementGateReady ? "remove-nurse-replacement" : undefined}
						sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, "&:empty": { display: "none" } }}
					>
						{renderFindReplacementForShift && (
							<SystemRoundedCheckbox
								checked={shouldFindReplacementForShift}
								onChange={setShouldFindReplacementForShift}
								disabled={!isReplacementGateReady}
								label={t("trigger.call.off.checkbox")}
								data-testid="delete-recurring-find-replacement-checkbox"
								// The call-off walkthrough's replacement step presses this on Next, flipping the tick
								// against the advice so its next step can show the note that appears.
								data-walkthrough-reveal="true"
							/>
						)}
						{/* Keyed on the gate's message (stable per gate result), opened by the tick — so ticking against
						    the advice folds the note in and out instead of jump-cutting it. */}
						{renderFindReplacementForShift && replacementAdvice?.message && (
							<InlineHint
								open={isReplacementHintOpen}
								tone={replacementAdvice.message.tone}
								dataTestId="delete-recurring-replacement-hint"
								data-annie-target="remove-nurse-replacement-note"
							>
								{t(replacementAdvice.message.messageKey)}
							</InlineHint>
						)}
					</Box>
					{shouldWarnRemovingUnion && (
						<Typography variant="body1" sx={{ color: "#eb5834" }}>
							{t("warn.removing.union")}
						</Typography>
					)}
				</Box>
			}
		/>
	);
};
